import { isHandled, today } from '../day'
import type { Item } from '../types'

export type ProjectTitles = Record<string, string>

/** Les métadonnées qui doivent rester lisibles d'un coup d'œil sur la carte. */
export function Tags({ item }: { item: Item }) {
  return (
    <>
      {item.deadline && <span className="tag">⏰ {item.deadline}</span>}
      {item.stake != null && <span className="tag">💶 {item.stake}€</span>}
      {item.recurrence && (
        <span className="tag">
          🔁 {item.recurrence.note ?? ''}/{item.recurrence.period ?? ''}
        </span>
      )}
      {item.time_estimate != null && <span className="tag">⏱ {item.time_estimate}min</span>}
      {item.note && (
        <span className="tag" title={item.note}>
          📝
        </span>
      )}
      {item.comments.length > 0 && <span className="tag">💬 {item.comments.length}</span>}
      {isHandled(item) ? (
        <span className="tag">✓ fait aujourd'hui</span>
      ) : (
        item.planned_for === today() && <span className="tag">☀ aujourd'hui</span>
      )}
    </>
  )
}

/** Rattachement visible : le projet s'il y en a un, sinon le domaine. */
export function AreaLabel({ item, projects }: { item: Item; projects: ProjectTitles }) {
  const project = item.project_id ? projects[item.project_id] : undefined
  const label = project ? '📁 ' + project : item.domain ? '◆ ' + item.domain : ''
  return label ? <span className="area">{label}</span> : null
}

interface CardProps {
  item: Item
  projects: ProjectTitles
  onEdit: (item: Item) => void
  onReclarify: (id: string) => void
  onDone?: (id: string) => void
  /** Planifier au tap : 'today' | 'tomorrow'. Absent = pas de boutons ☀/▷. */
  onPlan?: (id: string, date: 'today' | 'tomorrow') => void
}

/** Carte de prochaine action : action + badge projet/domaine + contexte. */
export function NextActionCard({ item, projects, onEdit, onReclarify, onDone, onPlan }: CardProps) {
  const action = item.next_action && item.next_action !== item.title ? item.next_action : item.title
  return (
    <div className="card">
      <div className="body">
        <div className="t">
          → {action}
          <Tags item={item} />
        </div>
        <div className="m">
          <AreaLabel item={item} projects={projects} />
          {item.context && <span className="pctx"> {item.context}</span>}
        </div>
      </div>
      {onPlan && (
        <>
          <button className="ebtn" onClick={() => onPlan(item.id, 'today')} title="Faire aujourd'hui">
            ☀
          </button>
          <button className="ebtn" onClick={() => onPlan(item.id, 'tomorrow')} title="Faire demain">
            ▷
          </button>
        </>
      )}
      <button className="ebtn" onClick={() => onReclarify(item.id)} title="Re-clarifier / transformer en projet">
        ↻
      </button>
      <button className="ebtn" onClick={() => onEdit(item)}>
        ✎
      </button>
      {onDone && (
        <button className="dbtn" onClick={() => onDone(item.id)}>
          ✓
        </button>
      )}
    </div>
  )
}

/** Carte générique (inbox, en attente, un jour). */
export function ItemCard({ item, projects, onEdit, onReclarify, onDone }: CardProps) {
  const action = item.next_action && item.next_action !== item.title ? item.next_action : item.title
  return (
    <div className="card">
      <div className="body">
        <div className="t">
          {action}
          <Tags item={item} /> <AreaLabel item={item} projects={projects} />
        </div>
        {action !== item.title && <div className="m">{item.title}</div>}
      </div>
      <button className="ebtn" onClick={() => onReclarify(item.id)} title="Re-clarifier / transformer en projet">
        ↻
      </button>
      <button className="ebtn" onClick={() => onEdit(item)}>
        ✎
      </button>
      {onDone && (
        <button className="dbtn" onClick={() => onDone(item.id)}>
          ✓
        </button>
      )}
    </div>
  )
}
